import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { EmptyArray, sum } from 'lib/array/arrayUtils'
import KeyValueSection from '../SearchInputTriggerKeyValue'
import BodyText from 'LuxKit/Typography/BodyText'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import AIGlowText from '../AIGlowText'
import Caption from 'LuxKit/Typography/Caption'

function Content({ showAIGlow }: { showAIGlow?: boolean }) {
  const intl = useIntl()
  const {
    occupancies = EmptyArray,
    disableSaleUnit,
  } = useContext(GlobalSearchStateContext)
  const containsChildren = occupancies.some(room => room.childrenAge?.length)
  const guestCount = useMemo(() => sum(occupancies, room => room.adults + (room.childrenAge?.length ?? 0)), [occupancies])

  const guestsLabel = intl.formatMessage({
    id: '0PyVbFYRn2Zo_9k9Oqj-',
    defaultMessage: 'Guests',
    description: 'Label for guests section in search travellers input',
  })

  const selectAdultsChildrenLabel = intl.formatMessage({
    id: 'Y3QL1w1uSmK8CuH9qcty',
    defaultMessage: 'Select adults/children',
    description: 'Placeholder text when no guests are selected in search travellers input',
  })

  let travellerText: string
  if (!occupancies.length) {
    travellerText = selectAdultsChildrenLabel
  } else if (containsChildren && disableSaleUnit) {
    travellerText = intl.formatMessage({
      id: 'vwTl8YQ9TNR15HigvEm_',
      defaultMessage: '{guestCount, plural, one {# guest} other {# guests}}',
      description: 'Guest count when occupancies include children and sale unit is disabled in search travellers input',
    }, { guestCount })
  } else if (containsChildren) {
    travellerText = intl.formatMessage({
      id: 'BWayv_-r-3zqtJ-myrPV',
      defaultMessage: '{guestCount, plural, one {# guest} other {# guests}} ({roomCount, plural, one {# room} other {# rooms}})',
      description: 'Guest count with room count when occupancies include children in search travellers input',
    }, { guestCount, roomCount: occupancies.length })
  } else if (disableSaleUnit) {
    travellerText = intl.formatMessage({
      id: '4wpbgM3U6VbvprPl35Dc',
      defaultMessage: '{guestCount, plural, one {# adult} other {# adults}}',
      description: 'Adult count when sale unit is disabled in search travellers input',
    }, { guestCount })
  } else {
    travellerText = intl.formatMessage({
      id: 'U99et4pR2z0bPxWOyNi4',
      defaultMessage: '{guestCount, plural, one {# adult} other {# adults}} ({roomCount, plural, one {# room} other {# rooms}})',
      description: 'Adult count with room count in search travellers input',
    }, { guestCount, roomCount: occupancies.length })
  }

  return (
    <KeyValueSection>
      <Caption colour="normal-contrast" variant="large" weight="bold">{guestsLabel}</Caption>
      <AIGlowText displayGlow={showAIGlow}>
        <BodyText variant="medium" colour="light-foreground-alt">
          {travellerText}
        </BodyText>
      </AIGlowText>
    </KeyValueSection>
  )
}

export default Content
